/**
 * Shared tour card / spotlight positioning — mobile-safe.
 * Avoids CSS transform on the positioned node so Framer Motion can animate freely.
 */

export const TOUR_EDGE = 16;
export const TOUR_CARD_WIDTH = 380;
export const TOUR_PAD = 10;

/** Clamp highlight rect into the visible viewport. */
export function clampTourRect(rect) {
  if (!rect || typeof window === 'undefined') return rect;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 8;
  let { top, left, width, height } = rect;

  left = Math.max(margin, left);
  top = Math.max(margin, top);
  width = Math.max(24, Math.min(width, vw - left - margin));
  height = Math.max(24, Math.min(height, vh - top - margin));

  if (left + width > vw - margin) {
    width = Math.max(24, vw - margin - left);
  }
  if (top + height > vh - margin) {
    height = Math.max(24, vh - top - margin);
  }

  return { top, left, width, height };
}

export function getElementTourRect(selector, pad = TOUR_PAD) {
  if (!selector || typeof document === 'undefined') return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return clampTourRect({
    top: r.top - pad,
    left: r.left - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  });
}

/**
 * Position styles for the tour card wrapper (no CSS transform).
 * On mobile, docks to top/bottom so left/right placements don't overflow.
 */
export function getTourCardSx({
  rect,
  placement = 'center',
  isMobile = false,
  zIndex = 1502,
}) {
  const edge = TOUR_EDGE;
  const gap = 14;
  const cardW = TOUR_CARD_WIDTH;
  const safeBottom = `max(${edge}px, env(safe-area-inset-bottom, 0px))`;
  const safeTop = `max(${edge}px, env(safe-area-inset-top, 0px))`;

  // —— Mobile: full-width dock (never side-place) ——
  if (isMobile) {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const targetLow =
      rect && placement !== 'center'
        ? rect.top + rect.height / 2 > vh * 0.52
        : false;

    if (!rect || placement === 'center') {
      // Centered overlay card — flex box, no transform
      return {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: `${edge}px`,
        py: `${edge}px`,
        zIndex,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      };
    }

    if (targetLow) {
      return {
        position: 'fixed',
        left: edge,
        right: edge,
        top: safeTop,
        width: 'auto',
        maxHeight: '44vh',
        overflowY: 'auto',
        zIndex,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      };
    }

    return {
      position: 'fixed',
      left: edge,
      right: edge,
      bottom: safeBottom,
      width: 'auto',
      maxHeight: '44vh',
      overflowY: 'auto',
      zIndex,
      pointerEvents: 'none',
      boxSizing: 'border-box',
    };
  }

  // —— Desktop / tablet ——
  if (!rect || placement === 'center') {
    return {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      zIndex,
      pointerEvents: 'none',
      boxSizing: 'border-box',
    };
  }

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxLeft = Math.max(edge, vw - cardW - edge);
  const maxTop = Math.max(edge, vh - 280);

  if (placement === 'bottom') {
    const top = Math.min(rect.top + rect.height + gap, maxTop);
    const left = Math.min(Math.max(edge, rect.left), maxLeft);
    return {
      position: 'fixed',
      top,
      left,
      width: cardW,
      zIndex,
      pointerEvents: 'none',
    };
  }

  if (placement === 'top') {
    const top = Math.max(edge, rect.top - gap - 220);
    const left = Math.min(Math.max(edge, rect.left), maxLeft);
    return {
      position: 'fixed',
      top,
      left,
      width: cardW,
      zIndex,
      pointerEvents: 'none',
    };
  }

  if (placement === 'left') {
    // Fall back below if not enough room on the left
    if (rect.left < cardW + gap + edge * 2) {
      return {
        position: 'fixed',
        top: Math.min(rect.top + rect.height + gap, maxTop),
        left: Math.min(Math.max(edge, rect.left), maxLeft),
        width: cardW,
        zIndex,
        pointerEvents: 'none',
      };
    }
    return {
      position: 'fixed',
      top: Math.max(edge, Math.min(rect.top, maxTop)),
      left: Math.max(edge, rect.left - gap - cardW),
      width: cardW,
      zIndex,
      pointerEvents: 'none',
    };
  }

  // right (default)
  if (rect.left + rect.width + gap + cardW > vw - edge) {
    return {
      position: 'fixed',
      top: Math.min(rect.top + rect.height + gap, maxTop),
      left: Math.min(Math.max(edge, rect.left), maxLeft),
      width: cardW,
      zIndex,
      pointerEvents: 'none',
    };
  }

  return {
    position: 'fixed',
    top: Math.max(edge, Math.min(rect.top, maxTop)),
    left: Math.min(rect.left + rect.width + gap, maxLeft),
    width: cardW,
    zIndex,
    pointerEvents: 'none',
  };
}

/** Inner card shell — re-enable pointer events; full width inside docked wrappers. */
export function getTourCardInnerSx(isMobile, isCentered) {
  return {
    pointerEvents: 'auto',
    width: isMobile || isCentered ? '100%' : TOUR_CARD_WIDTH,
    maxWidth: isMobile || isCentered ? 420 : TOUR_CARD_WIDTH,
    mx: isCentered ? 'auto' : 0,
  };
}
